import type { FilterValue } from '../../../shared/filters/collectionFilterQuery'
import {
	DEFAULT_COLLECTION_SORT,
	DEFAULT_MEDIA_FILTER,
	DEFAULT_STATUS_FILTER,
} from '../../../shared/filters/collectionFilters'
import type {
	CollectionSortFilterOptions,
	MediaFilterOptions,
	RatingFilterOptions,
	StatusFilterOptions,
} from '../../../shared/types/collection'

export type FiltersState = {
	sort?: CollectionSortFilterOptions
	favoritesOnly?: boolean
	media: FilterValue<MediaFilterOptions>
	rating?: FilterValue<RatingFilterOptions>
	folderId: FilterValue<number> | null
	status?: FilterValue<StatusFilterOptions>
	includeHidden?: boolean
	genre?: FilterValue<string>
	style?: FilterValue<string>
	yearFrom?: number
	yearTo?: number
	dimReleases?: boolean
}

export type FiltersOptions = {
	favoritesOnlyInStatus?: boolean
	showSort?: boolean
	showFavoritesOnly?: boolean
	showFolder?: boolean
	showMedia?: boolean
	showRating?: boolean
	showStatus?: boolean
	showGenre?: boolean
	showStyle?: boolean
	showYearRange?: boolean
	showIncludeHidden?: boolean
	genreOptions?: string[]
	styleOptions?: string[]
	yearRange?: { min: number | null; max: number | null }
	dimReleases?: boolean
	resetState?: Partial<FiltersState>
}

export const DEFAULT_FILTERS_STATE = {
	sort: DEFAULT_COLLECTION_SORT,
	favoritesOnly: false,
	media: DEFAULT_MEDIA_FILTER,
	rating: undefined,
	folderId: null,
	status: DEFAULT_STATUS_FILTER,
	includeHidden: false,
	genre: undefined,
	style: undefined,
	yearFrom: undefined,
	yearTo: undefined,
	dimReleases: true,
} satisfies FiltersState

export const DEFAULT_COLLECTION_FILTERS_STATE = {
	...DEFAULT_FILTERS_STATE,
} satisfies FiltersState

export const DEFAULT_SHUFFLE_FILTERS_STATE = {
	favoritesOnly: DEFAULT_FILTERS_STATE.favoritesOnly,
	media: DEFAULT_FILTERS_STATE.media,
	rating: DEFAULT_FILTERS_STATE.rating,
	folderId: DEFAULT_FILTERS_STATE.folderId,
	genre: DEFAULT_FILTERS_STATE.genre,
	style: DEFAULT_FILTERS_STATE.style,
	yearFrom: DEFAULT_FILTERS_STATE.yearFrom,
	yearTo: DEFAULT_FILTERS_STATE.yearTo,
	status: DEFAULT_FILTERS_STATE.status,
	includeHidden: DEFAULT_FILTERS_STATE.includeHidden,
} satisfies FiltersState

export function hasActiveFilters(
	state: FiltersState,
	defaults: Partial<FiltersState>,
): boolean {
	return (Object.keys(defaults) as Array<keyof FiltersState>).some(
		(key) =>
			JSON.stringify(normalizeComparableValue(state[key])) !==
			JSON.stringify(normalizeComparableValue(defaults[key])),
	)
}

function normalizeComparableValue(value: unknown): unknown {
	if (!Array.isArray(value)) {
		return typeof value === 'number' ? String(value) : (value ?? null)
	}
	const normalized = [...value].map(String).sort()
	return normalized.length === 1 ? normalized[0] : normalized
}
